import React from 'react';

import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { FileExtensionResult } from 'stores/WorkSpaceStoreAdapter';

import ExtensionMenu from './ExtensionMenu';
import TreeNode, { TreeDnd } from './TreeNode';
import { TreeNodeData } from './treeUtils';

type Props = {
  tree: TreeNodeData[];
  loading: boolean;
  selectedId: string | null;
  expandedIds: Set<string>;
  /** 記事として公開済みのファイル fileId 集合（公開済みバッジ表示用） */
  articleFileIds: Set<string>;
  /** 操作可否（他人のワークスペースで編集不可の場合は false） */
  editable: boolean;
  /** 作成可能な拡張子一覧（新規ファイル作成メニュー用） */
  extensions: FileExtensionResult[];
  /** ヘッダーに表示するタイトル（対象ワークスペースのユーザーID / MyWorkspace） */
  title: string;
  onToggle: (id: string) => void;
  onSelect: (node: TreeNodeData) => void;
  onOpenFile: (node: TreeNodeData) => void;
  onContextMenu: (e: React.MouseEvent, node: TreeNodeData) => void;
  onNewFolder: () => void;
  /** 拡張子を選んでファイル作成（extension は 'java' などの小文字） */
  onNewFile: (extension: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  /** D&D 移動ハンドラ群 */
  dnd: TreeDnd;
  /** 空白/ルート領域へのドロップ（ルート直下へ移動）。editable かつドラッグ中のみ有効 */
  onDropToRoot: () => void;
};

/**
 * エクスプローラーパネル（左ペイン）。ツリー表示 + 上部アイコン。
 */
const ExplorerPanel: React.FC<Props> = ({
  tree,
  loading,
  selectedId,
  expandedIds,
  articleFileIds,
  editable,
  extensions,
  title,
  onToggle,
  onSelect,
  onOpenFile,
  onContextMenu,
  onNewFolder,
  onNewFile,
  onExpandAll,
  onCollapseAll,
  dnd,
  onDropToRoot,
}) => {
  // 新規ファイル作成の拡張子選択メニューのアンカー
  const [newFileAnchor, setNewFileAnchor] = React.useState<HTMLElement | null>(null);
  // ルート領域（空白）へのドロップ受け入れ中フラグ
  const [rootDropActive, setRootDropActive] = React.useState(false);

  // 空白領域は「ルート直下へ移動」のドロップ先。ドラッグ中のみ受け入れる。
  const handleRootDragOver = (e: React.DragEvent) => {
    if (!dnd.enabled || !dnd.draggingId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setRootDropActive(true);
  };
  const handleRootDrop = (e: React.DragEvent) => {
    if (!dnd.enabled || !dnd.draggingId) return;
    e.preventDefault();
    setRootDropActive(false);
    onDropToRoot();
  };

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      {/* ヘッダー */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', gap: 0.5 }}
      >
        <Tooltip title={title}>
          <Typography
            variant="caption"
            fontWeight={600}
            noWrap
            sx={{ color: 'text.secondary', letterSpacing: '0.05em', flex: 1, minWidth: 0 }}
          >
            {title}
          </Typography>
        </Tooltip>
        <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
          <Tooltip title="新規フォルダ作成">
            <span>
              <IconButton size="small" onClick={onNewFolder} disabled={!editable}>
                <CreateNewFolderOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="新規ファイル作成">
            <span>
              <IconButton
                size="small"
                onClick={(e) => setNewFileAnchor(e.currentTarget)}
                disabled={!editable}
              >
                <NoteAddOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="全て開く">
            <IconButton size="small" onClick={onExpandAll}>
              <UnfoldMoreIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="全て閉じる">
            <IconButton size="small" onClick={onCollapseAll}>
              <UnfoldLessIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* ツリー（横方向にはみ出す場合は横スクロール可能）。空白領域はルート移動のドロップ先 */}
      <Box
        onDragOver={handleRootDragOver}
        onDragLeave={() => setRootDropActive(false)}
        onDrop={handleRootDrop}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          py: 0.5,
          bgcolor: rootDropActive ? 'action.hover' : 'transparent',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : tree.length === 0 ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 1.5, py: 1, display: 'block' }}
          >
            ファイルがありません
          </Typography>
        ) : (
          // 行を内容幅にして、はみ出したら親を横スクロールできるようにする
          // （短いツリーでもパネル幅いっぱいに背景を広げるため minWidth: 100%）
          <Box sx={{ minWidth: 'max-content' }}>
            {tree.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                depth={0}
                selectedId={selectedId}
                expandedIds={expandedIds}
                articleFileIds={articleFileIds}
                onToggle={onToggle}
                onSelect={onSelect}
                onOpenFile={onOpenFile}
                onContextMenu={onContextMenu}
                dnd={dnd}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* 新規ファイル作成: 拡張子選択メニュー */}
      <ExtensionMenu
        anchorEl={newFileAnchor}
        extensions={extensions}
        onClose={() => setNewFileAnchor(null)}
        onSelect={(extension) => onNewFile(extension)}
      />
    </Stack>
  );
};

export default ExplorerPanel;
